import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Editor from '@monaco-editor/react';
import toast from 'react-hot-toast';

interface JsonEditorProps {
  value: Record<string, any> | string;
  onChange?: (value: Record<string, any>) => void;
  height?: string;
  readOnly?: boolean;
  showFormatButton?: boolean;
  showCopyButton?: boolean;
  theme?: 'light' | 'dark';
}

export const JsonEditor: React.FC<JsonEditorProps> = React.memo(({
  value,
  onChange,
  height = 'min(60vh, 600px)',
  readOnly = false,
  showFormatButton = true,
  showCopyButton = true,
  theme,
}) => {
  const [jsonString, setJsonString] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isInternalUpdate = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Auto-detect system theme preference
  const prefersDark = useMemo(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  }, []);
  const editorTheme = theme ?? (prefersDark ? 'dark' : 'light');

  // Initialize and sync external value changes (FIXED: proper dependency)
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    try {
      const formatted = typeof value === 'string' 
        ? value 
        : JSON.stringify(value, null, 2);
      
      // Prevent unnecessary state updates
      setJsonString(prev => prev !== formatted ? formatted : prev);
      setError(null);
    } catch {
      setError('Invalid JSON format');
    }
  }, [value]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Handle editor changes with debouncing for performance
  const handleEditorChange = useCallback((newValue: string | undefined) => {
    if (newValue === undefined) return;
    
    isInternalUpdate.current = true;
    setJsonString(newValue);
    
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Debounce parsing for performance (300ms)
    debounceRef.current = setTimeout(() => {
      try {
        const parsed = JSON.parse(newValue);
        setError(null);
        onChange?.(parsed);
      } catch {
        setError('Invalid JSON syntax');
      }
    }, 300);
  }, [onChange]);

  // Format JSON with proper error handling
  const handleFormat = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonString);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonString(formatted);
      setError(null);
      toast.success('JSON formatted successfully');
      
      // Update parent with formatted value
      onChange?.(parsed);
    } catch {
      toast.error('Cannot format invalid JSON');
    }
  }, [jsonString, onChange]);

  // Copy to clipboard with modern API
  const handleCopy = useCallback(() => {
    if (!navigator.clipboard) {
      toast.error('Clipboard not supported in this browser');
      return;
    }

    navigator.clipboard.writeText(jsonString)
      .then(() => toast.success('Copied to clipboard'))
      .catch(() => toast.error('Failed to copy'));
  }, [jsonString]);

  // Memoize editor options for performance
  const editorOptions = useMemo(() => ({
    readOnly,
    minimap: { enabled: false },
    automaticLayout: true,
    tabSize: 2,
    formatOnPaste: true,
    formatOnType: true,
    scrollBeyondLastLine: false,
    fontSize: 14,
    lineNumbers: 'off' as const,
    renderLineHighlight: 'all' as const,
    scrollbar: {
      vertical: 'auto' as const,
      horizontal: 'auto' as const,
    },
    quickSuggestions: true,
    suggestOnTriggerCharacters: true,
    acceptSuggestionOnEnter: 'on' as const,
  }), [readOnly]);

  return (
    <div className="w-full flex flex-col gap-3" role="region" aria-label="JSON Editor">
      {/* Action Buttons - Responsive */}
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {showFormatButton && !readOnly && (
            <button
              onClick={handleFormat}
              className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 w-full sm:w-auto"
              aria-label="Format JSON"
              type="button"
            >
              Format JSON
            </button>
          )}
          {showCopyButton && (
            <button
              onClick={handleCopy}
              className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors flex items-center justify-center space-x-1 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 w-full sm:w-auto"
              aria-label="Copy JSON to clipboard"
              type="button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span>Copy</span>
            </button>
          )}
        </div>
        
        {/* Error Display - Improved A11y */}
        {error && (
          <div 
            id="json-error"
            className="text-xs sm:text-sm text-red-600 flex items-center gap-1" 
            role="alert" 
            aria-live="polite"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>{error}</span>
          </div>
        )}
      </header>

      {/* Monaco Editor - Responsive with improved A11y */}
      <section 
        style={{ height }}
        className="w-full rounded-lg border border-gray-300 overflow-hidden bg-[var(--bg-secondary)]"
        aria-label="Code editor"
        aria-invalid={!!error}
        aria-describedby={error ? 'json-error' : undefined}
      >
        <Editor
          height="100%"
          defaultLanguage="json"
          value={jsonString}
          onChange={handleEditorChange}
          theme={editorTheme === 'dark' ? 'vs-dark' : 'vs-light'}
          options={editorOptions}
          loading={
            <div className="animate-pulse flex items-center justify-center h-full text-sm text-gray-500">
              Loading editor...
            </div>
          }
        />
      </section>
    </div>
  );
});

JsonEditor.displayName = 'JsonEditor';
